import React from "react";

const ConditionsModal = ({ isOpen, onClose, type }) => {
  if (!isOpen) return null;

  const content = {
    terms: (
      <>
        <header className="w-full">
          <div className="max-w-5xl mx-auto py-4 px-6">
            <h1 className="text-2xl font-semibold text-black underline">
              Terms and Conditions
            </h1>
          </div>
        </header>

        <main className="px-6 max-w-5xl mx-auto">
          <p className="text-sm text-gray-600 mb-4">
            Last Updated: May 20, 2025
          </p>

          <p className="mb-4">
            Welcome to Onelyk (“we,” “our,” or “us”). These Terms and Conditions
            (“Terms”) govern your access to and use of our guest engagement and
            review management software platform (“Service”) provided to hotel
            owners and operators (“you,” “your,” “Customer”).
          </p>

          <p className="mb-4">
            By accessing or using the Service, you agree to be bound by these
            Terms. If you do not agree, you may not use the Service.
          </p>

          <section className="mb-6">
            <h2 className="text-lg font-semibold mb-2">1. Service Overview</h2>
            <p className="mb-2">
              Onelyk provides a software-as-a-service (SaaS) platform that
              enables hotel owners to:
            </p>
            <ul className="list-disc list-inside space-y-1">
              <li>
                Engage directly with guests through SMS, email, QR code, or
                in-room interactions.
              </li>
              <li>
                Automate guest messaging before, during, and after the stay.
              </li>
              <li>
                Collect, manage, and respond to reviews from platforms such as
                Google, TripAdvisor, Expedia, and Booking.com.
              </li>
              <li>
                Monitor guest feedback, team performance, and reputation KPIs
                through a centralized dashboard.
              </li>
            </ul>
          </section>

          <section className="mb-6">
            <h2 className="text-lg font-semibold mb-2">
              2. Data Privacy & PII Compliance
            </h2>
            <p className="mb-2">
              We understand the critical importance of guest data privacy,
              especially Personally Identifiable Information (PII). We take all
              reasonable steps to ensure that your guests’ data is handled in
              accordance with applicable laws, including but not limited to
              GDPR, CCPA, and other data protection regulations.
            </p>
            <ul className="list-disc list-inside space-y-1">
              <li>
                <strong>Data Collection:</strong> We collect guest data only as
                necessary to deliver the Service.
              </li>
              <li>
                <strong>Data Usage:</strong> Guest data will only be used for
                the purposes of guest engagement and review collection, as
                defined by the Customer.
              </li>
              <li>
                <strong>Third-Party Sharing:</strong> We do not sell or share
                guest PII with third parties, except with integrated OTAs (e.g.,
                Expedia) or review platforms as authorized by you.
              </li>
              <li>
                <strong>Data Storage:</strong> All data is stored securely using
                encrypted infrastructure, hosted on trusted cloud providers
                (e.g., AWS).
              </li>
              <li>
                <strong>Breach Notification:</strong> In the event of a data
                breach affecting PII, we will notify you within 72 hours.
              </li>
            </ul>
          </section>

          <section className="mb-6">
            <h2 className="text-lg font-semibold mb-2">
              3. Customer Responsibilities
            </h2>
            <p className="mb-2">As a Customer, you agree to:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Provide accurate information when creating your account.</li>
              <li>
                Obtain consent from your guests before sending them marketing or
                review-related communication, as required by law.
              </li>
              <li>
                Use the Service only for lawful hotel or hospitality-related
                purposes.
              </li>
              <li>Maintain the confidentiality of your account credentials.</li>
            </ul>
          </section>

          <section className="mb-6">
            <h2 className="text-lg font-semibold mb-2">
              4. Third-Party Integrations
            </h2>
            <p className="mb-2">
              The Service may integrate with third-party services such as OTAs
              (Expedia, Booking.com), review platforms (Google, Yelp), and
              communication tools (SMS/email providers).
            </p>
            <p className="mb-2">
              We are not responsible for the functionality, data policies, or
              terms of use of these third-party platforms. You are responsible
              for complying with the terms of those platforms when using our
              integrations.
            </p>
          </section>

          <section className="mb-6">
            <h2 className="text-lg font-semibold mb-2">
              5. Intellectual Property
            </h2>
            <p>
              All content, software, and technologies provided as part of the
              Service are the property of Onelyk or its licensors. You may not
              copy, reproduce, or reverse engineer any part of the Service.
            </p>
          </section>

          <section className="mb-6">
            <h2 className="text-lg font-semibold mb-2">6. Fees and Billing</h2>
            <p>
              Use of the Service may be subject to subscription fees or
              service-based charges. Billing terms will be outlined in a
              separate agreement or order form. Non-payment may result in
              suspension or termination of access.
            </p>
          </section>

          <section className="mb-6">
            <h2 className="text-lg font-semibold mb-2">7. Termination</h2>
            <p>
              We may terminate or suspend your account if you violate these
              Terms or use the Service for unlawful or abusive purposes. Upon
              termination, access to your account and data may be restricted.
            </p>
          </section>

          <section className="mb-6">
            <h2 className="text-lg font-semibold mb-2">
              8. Limitation of Liability
            </h2>
            <p>
              To the maximum extent permitted by law, Onelyk shall not be liable
              for any indirect, incidental, or consequential damages, including
              loss of revenue or data, arising from the use of our Service.
            </p>
          </section>

          <section className="mb-6">
            <h2 className="text-lg font-semibold mb-2">9. Indemnification</h2>
            <p>
              You agree to indemnify and hold Onelyk harmless from any claims,
              liabilities, or damages arising from your misuse of the Service or
              violation of these Terms.
            </p>
          </section>

          <section className="mb-6">
            <h2 className="text-lg font-semibold mb-2">10. Changes to Terms</h2>
            <p>
              We may update these Terms from time to time. You will be notified
              of any material changes, and continued use of the Service
              constitutes your acceptance of the updated Terms.
            </p>
          </section>

          <section className="mb-6">
            <h2 className="text-lg font-semibold mb-2">
              11. Contact Information
            </h2>
            <p>
              Onelyk Inc.
              <br />
              Email:{" "}
              <a
                href="mailto:support@onelyk.com"
                className="text-blue-600 hover:underline"
              >
                support@onelyk.com
              </a>
              <br />
            </p>
          </section>
        </main>
      </>
    ),
    privacy: (
      <>
        <header className="w-full">
          <div className="max-w-5xl mx-auto py-4 px-6">
            <h1 className="text-2xl font-semibold text-black underline">
              Privacy Policy
            </h1>
          </div>
        </header>

        <main className="px-6 max-w-5xl mx-auto">
          <p className="text-sm text-gray-600 mb-4">
            Last Updated: May 20, 2025
          </p>

          <p className="mb-4">
            Onelyk (“we,” “our,” or “us”) is committed to protecting the privacy
            of our customers and their guests. This Privacy Policy describes how
            we collect, use, disclose, and safeguard personal information
            through our guest engagement and review management software.
          </p>

          <section className="mb-6">
            <h2 className="text-lg font-semibold mb-2">
              1. Information We Collect
            </h2>
            <p className="mb-2">
              We may collect the following types of information:
            </p>
            <ul className="list-disc list-inside space-y-1">
              <li>
                Guest contact details: Name, email, phone number, and booking
                information.
              </li>
              <li>
                Usage data: Interactions with our platform and communication
                history.
              </li>
              <li>
                Hotel property and staff data necessary to support your account.
              </li>
            </ul>
          </section>

          <section className="mb-6">
            <h2 className="text-lg font-semibold mb-2">
              2. How We Use Information
            </h2>
            <p className="mb-2">We use guest and customer data to:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Facilitate guest communications and automated messaging.</li>
              <li>
                Collect and manage guest reviews across third-party platforms.
              </li>
              <li>
                Analyze performance metrics and optimize guest engagement.
              </li>
            </ul>
          </section>

          <section className="mb-6">
            <h2 className="text-lg font-semibold mb-2">
              3. Legal Basis for Processing
            </h2>
            <p className="mb-2">
              We rely on consent (where required), contract obligations, and
              legitimate interests to collect and use personal data.
            </p>
          </section>

          <section className="mb-6">
            <h2 className="text-lg font-semibold mb-2">
              4. Sharing of Information
            </h2>
            <p>
              We do not sell guest or customer data. We may share data with:
            </p>
            <ul className="list-disc list-inside space-y-1">
              <li>
                Review platforms (e.g., Google, TripAdvisor) as authorized by
                you.
              </li>
              <li>
                Third-party vendors or services required to operate our
                software.
              </li>
            </ul>
          </section>

          <section className="mb-6">
            <h2 className="text-lg font-semibold mb-2">5. Data Security</h2>
            <p>
              We implement reasonable technical and organizational measures to
              protect data, including encryption, access controls, and secure
              cloud infrastructure (e.g., AWS).
            </p>
          </section>

          <section className="mb-6">
            <h2 className="text-lg font-semibold mb-2">6. Data Retention</h2>
            <p>
              We retain guest and user data only for as long as necessary to
              fulfill the purposes described, unless a longer retention period
              is required by law.
            </p>
          </section>

          <section className="mb-6">
            <h2 className="text-lg font-semibold mb-2">7. Your Rights</h2>
            <p>
              Depending on your location, you may have rights to access,
              correct, delete, or restrict personal data we hold about you. To
              exercise these rights, please contact us.
            </p>
          </section>

          <section className="mb-6">
            <h2 className="text-lg font-semibold mb-2">
              8. Children’s Privacy
            </h2>
            <p>
              Our platform is not directed at individuals under 18. We do not
              knowingly collect personal data from minors.
            </p>
          </section>

          <section className="mb-6">
            <h2 className="text-lg font-semibold mb-2">
              9. Changes to This Policy
            </h2>
            <p>
              We may update this Privacy Policy periodically. Continued use of
              our Service after changes means you accept the updated terms.
            </p>
          </section>

          <section className="mb-6">
            <h2 className="text-lg font-semibold mb-2">
              10. Contact Information
            </h2>
            <p>
              Onelyk Inc.
              <br />
              Email:{" "}
              <a
                href="mailto:support@onelyk.com"
                className="text-blue-600 hover:underline"
              >
                support@onelyk.com
              </a>
              <br />
            </p>
          </section>
        </main>
      </>
    ),
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-start pt-20 z-50">
      <div className="relative bg-white text-black p-6 rounded-lg max-w-xl w-full mx-4 max-h-[80vh] overflow-y-auto">
        {/* Close X Button */}
        <button
          onClick={onClose}
          className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 text-xl font-bold"
          aria-label="Close"
        >
          &times;
        </button>

        {content[type]}
      </div>
    </div>
  );
};

export default ConditionsModal;
